import { connect } from 'react-redux';
import { routeTo } from 'redux-router-kit';

import * as CliApp from 'app/entities/CliApp';
import * as CliAppDefinition from 'app/entities/CliAppDefinition';
import * as DeveloperActions from 'app/developer-v3/Actions';
import VersionsPage from 'app/developer-v3/pages/versions/VersionsPage';
import { actions as ManageAppActions } from 'app/developer-v3/store/manage';
import { sortVersionObjects } from 'app/developer-v3/utils';
import {
  currentAppEntity,
  currentAppId,
  currentAppVersionEntities,
  lifecycleErrorMessage as getLifecycleErrorMessage,
  lifecycleHasFailed,
  lifecycleHasFinished,
  lifecycleHasStarted,
} from 'app/developer-v3/selectors';
import { BASE_APP_URL, DEPLOYMENT_STATUSES } from 'app/developer-v3/constants';

import type { SemVer } from 'app/developer-v3/types/version';

const mapStateToProps = state => {
  const app = currentAppEntity(state);
  const isLoading =
    CliAppDefinition.selectors.all.isLoading(state) ||
    CliApp.selectors.all.isLoading(state);

  const versionEntities = currentAppVersionEntities(state);
  const versions = versionEntities.map(versionEntity => ({
    authorEmail: CliAppDefinition.selectors.authorEmail(versionEntity),
    changelog: CliAppDefinition.selectors.changelog(versionEntity),
    createdAt: CliAppDefinition.selectors.createdAt(versionEntity),
    deployment: CliAppDefinition.selectors.deployment(versionEntity),
    deprecationDate: CliAppDefinition.selectors.deprecationDate(versionEntity),
    integratoinId: CliAppDefinition.selectors.appId(versionEntity),
    isGUIIntegration: !!CliAppDefinition.selectors.isGUIIntegration(
      versionEntity
    ),
    lifecycle: CliAppDefinition.selectors.lifecycle(versionEntity),
    platformVersion: CliAppDefinition.selectors.platformVersion(versionEntity),
    submitStatus: CliAppDefinition.selectors.submitStatus(versionEntity),
    userCount: CliAppDefinition.selectors.userCount(versionEntity),
    userCountUpdatedAt: CliAppDefinition.selectors.userCountUpdatedAt(
      versionEntity
    ),
    updatedAt: CliAppDefinition.selectors.updatedAt(versionEntity),
    version: CliAppDefinition.selectors.version(versionEntity),
  }));

  return {
    versions: sortVersionObjects(versions),
    versionsActive: versions.filter(
      version => version.deployment !== DEPLOYMENT_STATUSES.deprecated
    ),
    integrationId: currentAppId(state),
    integrationStatus: CliApp.selectors.status(app),
    isLoading,
    isLifecycleRequestStarted: lifecycleHasStarted(state),
    isLifecycleRequestSuccessful: lifecycleHasFinished(state),
    isLifecycleRequestFailed: lifecycleHasFailed(state),
    isPublished: CliApp.selectors.isPublished(app),
    isReadyToActivate: CliApp.selectors.isReadyToActivate(app),
    lifecycleErrorMessage: getLifecycleErrorMessage(state),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Added for easier testing. connectRouteTo makes mocking the route call difficult.
  routeTo(...args) {
    dispatch(routeTo(...args));
  },

  viewAppVersion(appVersion: SemVer) {
    dispatch(DeveloperActions.viewAppVersion(appVersion, undefined));
  },

  onClone(integrationId: number, fromVersion: SemVer, toVersion: SemVer) {
    dispatch(
      DeveloperActions.cloneAppVersion(integrationId, fromVersion, toVersion)
    );
  },

  onMigrate(
    integrationId: number,
    fromVersion: SemVer,
    toVersion: SemVer,
    options: { percentage?: number; email?: string } = {}
  ) {
    dispatch(
      DeveloperActions.migrateAppVersion(
        integrationId,
        fromVersion,
        toVersion,
        options
      )
    );
  },

  onDeprecate(
    integrationId: number,
    versionNumber: SemVer,
    date: string,
    reason?: string
  ) {
    dispatch(
      DeveloperActions.deprecateAppVersion(
        integrationId,
        versionNumber,
        date,
        reason
      )
    );
  },

  onLegacy(integrationId: number, versionNumber: SemVer) {
    dispatch(
      DeveloperActions.setAsLegacyAppVersion(integrationId, versionNumber)
    );
  },

  async onDelete(integrationId: number, versionToDelete?: SemVer) {
    if (!versionToDelete) {
      await dispatch(DeveloperActions.deleteApp(integrationId));
      await dispatch(routeTo(`${BASE_APP_URL}/`));
    } else {
      await dispatch(
        DeveloperActions.deleteAppVersion(integrationId, versionToDelete)
      );
      await dispatch(CliApp.actions.loadCollectionWithParams({ full: true }));
    }
  },

  refreshVersionList() {
    const {
      params: { id: integrationId },
    } = ownProps;
    return dispatch(DeveloperActions.loadAppAndAllVersions(integrationId));
  },

  resetLifecycle() {
    dispatch(DeveloperActions.resetLifecycle());
  },

  resetAppStatus() {
    return dispatch(ManageAppActions.resetCurrentAppStatus());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(VersionsPage);
